import { useState, useEffect } from "react";
import { Activity, Timer, TrendingUp, Zap, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BentoGrid, BentoGridItem } from "@/components/ui/bento-grid";
import { useRequestCounter } from "@/hooks/useRequestCounter";

interface RequestCounterProps {
  showDNS?: boolean;
  showAPI?: boolean;
  enableWarnings?: boolean;
  refreshInterval?: number;
}

export function RequestCounter({ 
  showDNS = true, 
  showAPI = true, 
  enableWarnings = true,
  refreshInterval = 1000
}: RequestCounterProps) {
  const {
    requestStats,
    trackDNSRequest,
    trackAPIRequest,
    getUsagePercentage,
    getTimeUntilReset,
    resetCounters,
    getStatsSummary
  } = useRequestCounter({
    enableWarnings,
    warningThresholds: {
      dns: { perSecond: 100, perMinute: 1000, perHour: 10000, perDay: 50000 },
      api: { perSecond: 10, perMinute: 100, perHour: 1000, perDay: 5000 }
    }
  });

  const [isSimulating, setIsSimulating] = useState(false);

  // Auto-refresh display
  useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render to update time displays
    }, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const formatTimeRemaining = (milliseconds: number) => {
    if (milliseconds < 1000) return `${Math.ceil(milliseconds / 100) * 100}ms`;
    if (milliseconds < 60000) return `${Math.ceil(milliseconds / 1000)}s`;
    if (milliseconds < 3600000) return `${Math.ceil(milliseconds / 60000)}m`;
    if (milliseconds < 86400000) return `${Math.ceil(milliseconds / 3600000)}h`;
    return `${Math.ceil(milliseconds / 86400000)}d`;
  };

  const simulateLoad = async () => {
    setIsSimulating(true);
    
    // Simulate different load patterns
    const patterns = [
      { dns: 5, api: 1, interval: 100, duration: 2000 }, // Burst
      { dns: 2, api: 0, interval: 200, duration: 3000 }, // Steady DNS
      { dns: 1, api: 1, interval: 300, duration: 2000 }, // Mixed load
    ];

    for (const pattern of patterns) {
      const startTime = Date.now();
      
      while (Date.now() - startTime < pattern.duration) {
        if (showDNS && pattern.dns > 0) {
          trackDNSRequest(pattern.dns);
        }
        if (showAPI && pattern.api > 0) {
          trackAPIRequest(pattern.api);
        }
        
        await new Promise(resolve => setTimeout(resolve, pattern.interval));
      }
      
      // Small break between patterns
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setIsSimulating(false);
  };

  const renderUsageWindow = (
    type: 'dns' | 'api',
    window: 'second' | 'minute' | 'hour' | 'day',
    label: string
  ) => {
    const count = requestStats[type][window].count;
    const resetTime = getTimeUntilReset(type, window);
    const percentage = getUsagePercentage(type, window);
    
    const getStatusColor = (percent: number) => {
      if (percent >= 90) return "text-red-500";
      if (percent >= 75) return "text-yellow-500";
      return "text-green-500";
    };

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{label}</span>
          <span className={`font-medium ${getStatusColor(percentage)}`}>
            {count}
          </span>
        </div>
        <Progress value={percentage} className="h-2" />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Resets in {formatTimeRemaining(resetTime)}</span>
          <span>{Math.round(percentage)}%</span>
        </div>
      </div>
    );
  };

  const summary = getStatsSummary();

  return (
    <div className="space-y-6">
      <Card className="bg-background/80 backdrop-blur-xl border border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Activity className="h-5 w-5 text-primary" />
              <CardTitle className="text-foreground">Request Counter</CardTitle>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                onClick={() => resetCounters()} 
                variant="outline" 
                size="sm"
                className="text-xs"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Reset All
              </Button>
              <Button 
                onClick={simulateLoad} 
                variant="outline" 
                size="sm"
                disabled={isSimulating}
                className="text-xs"
              >
                <Zap className="h-3 w-3 mr-1" />
                {isSimulating ? 'Simulating...' : 'Simulate Load'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground mb-6">
            Real-time tracking of DNS queries and API requests with rate limit monitoring.
          </div>

          <BentoGrid className="grid-cols-1 lg:grid-cols-2 gap-6">
            {showDNS && (
              <BentoGridItem
                className="lg:col-span-1"
                icon={<Activity className="h-4 w-4" />}
                glowColor="#00F5A0"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-foreground flex items-center space-x-2">
                      <Activity className="h-4 w-4" />
                      <span>DNS Queries</span>
                    </h4>
                    <Badge variant="outline" className="text-green-500 border-green-500/30 text-xs">
                      {summary.dns.total} total
                    </Badge>
                  </div>

                  <div className="space-y-4">
                    {renderUsageWindow('dns', 'second', 'Per Second')}
                    {renderUsageWindow('dns', 'minute', 'Per Minute')}
                    {renderUsageWindow('dns', 'hour', 'Per Hour')}
                    {renderUsageWindow('dns', 'day', 'Per Day')}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <span className="text-xs text-muted-foreground">Peak: {summary.dns.highest}</span>
                    <Button 
                      onClick={() => resetCounters('dns')} 
                      variant="ghost" 
                      size="sm"
                      className="text-xs h-6"
                    >
                      Reset DNS
                    </Button>
                  </div>
                </div>
              </BentoGridItem>
            )}

            {showAPI && (
              <BentoGridItem
                className="lg:col-span-1"
                icon={<TrendingUp className="h-4 w-4" />}
                glowColor="#8B5CF6"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-foreground flex items-center space-x-2">
                      <TrendingUp className="h-4 w-4" />
                      <span>API Requests</span>
                    </h4>
                    <Badge variant="outline" className="text-purple-500 border-purple-500/30 text-xs">
                      {summary.api.total} total
                    </Badge>
                  </div>

                  <div className="space-y-4">
                    {renderUsageWindow('api', 'second', 'Per Second')}
                    {renderUsageWindow('api', 'minute', 'Per Minute')}
                    {renderUsageWindow('api', 'hour', 'Per Hour')}
                    {renderUsageWindow('api', 'day', 'Per Day')}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <span className="text-xs text-muted-foreground">Peak: {summary.api.highest}</span>
                    <Button 
                      onClick={() => resetCounters('api')} 
                      variant="ghost" 
                      size="sm"
                      className="text-xs h-6"
                    >
                      Reset API
                    </Button>
                  </div>
                </div>
              </BentoGridItem>
            )}
          </BentoGrid>

          {enableWarnings && (
            <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <div className="flex items-start space-x-2">
                <Timer className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-medium text-yellow-500 mb-1">Rate Limit Warnings Enabled</div>
                  <div className="text-muted-foreground">
                    You'll receive notifications when approaching 90% of configured rate limits.
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}